// src/index.ts
import { useEffect, useState } from "react";
var PlatformApiError = class extends Error {
  constructor(code, message, status) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = "PlatformApiError";
  }
  code;
  status;
};
var PlatformApiClient = class {
  constructor(baseUrl = "") {
    this.baseUrl = baseUrl;
  }
  baseUrl;
  getSession() {
    return this.request("/api/v1/session", { allowUnauthorized: true });
  }
  getProviders() {
    return this.request("/api/v1/auth/providers");
  }
  register(request) {
    return this.request("/api/v1/auth/register", { method: "POST", body: request });
  }
  login(request) {
    return this.request("/api/v1/auth/login", { method: "POST", body: request });
  }
  logout() {
    return this.request("/api/v1/auth/logout", { method: "POST" });
  }
  getGames() {
    return this.request("/api/v1/games");
  }
  getGamePulse() {
    return this.request("/api/v1/games/pulse");
  }
  getWallet() {
    return this.request("/api/v1/wallet");
  }
  launchGame(gameId) {
    return this.request(`/api/v1/games/${encodeURIComponent(gameId)}/launch`, { method: "POST" });
  }
  reportGuestPresence(gameId, request = {}) {
    return this.request(`/api/v1/games/${encodeURIComponent(gameId)}/presence`, { method: "POST", body: request });
  }
  async request(path, options = {}) {
    const response = await fetch(new URL(path, this.baseUrl || window.location.origin), {
      method: options.method ?? "GET",
      credentials: "include",
      headers: options.body === void 0 ? void 0 : { "content-type": "application/json" },
      body: options.body === void 0 ? void 0 : JSON.stringify(options.body)
    });
    if (response.status === 401 && options.allowUnauthorized) return null;
    const result = await response.json();
    if (!response.ok || !result.ok) {
      throw new PlatformApiError(
        result.ok ? "REQUEST_FAILED" : result.error.code,
        result.ok ? `\u8BF7\u6C42\u5931\u8D25\uFF1A${response.status}` : result.error.message,
        response.status
      );
    }
    return result.data;
  }
};
var gameBootstrapPromise = null;
function useGamePlatformSession() {
  const [state, setState] = useState({ status: "loading", principal: null, error: null });
  useEffect(() => {
    let cancelled = false;
    gameBootstrapPromise ??= bootstrapGameSession();
    void gameBootstrapPromise.then((principal) => {
      if (!cancelled) setState({ status: "ready", principal, error: null });
    }).catch((error) => {
      if (!cancelled) setState({ status: "error", principal: null, error: error instanceof Error ? error.message : "\u65E0\u6CD5\u5EFA\u7ACB\u6E38\u620F\u4F1A\u8BDD" });
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}
async function bootstrapGameSession() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("launch_code");
  const response = await fetch(resolveGameEndpoint("api/platform/session"), {
    method: code ? "POST" : "GET",
    credentials: "include",
    headers: code ? { "content-type": "application/json" } : void 0,
    body: code ? JSON.stringify({ code }) : void 0
  });
  const result = await response.json();
  if (!response.ok || !result.ok) throw new PlatformApiError(result.ok ? "GAME_SESSION_FAILED" : result.error.code, result.ok ? "\u65E0\u6CD5\u5EFA\u7ACB\u6E38\u620F\u4F1A\u8BDD" : result.error.message, response.status);
  if (code) {
    url.searchParams.delete("launch_code");
    window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }
  return result.data;
}
function resolveGameEndpoint(relativePath, currentUrl = window.location.href) {
  const normalizedPath = relativePath.replace(/^\/+/, "");
  return new URL(normalizedPath, new URL(".", currentUrl));
}
function resolveGameSocketPath(currentUrl = window.location.href) {
  return resolveGameEndpoint("socket.io", currentUrl).pathname;
}
export {
  PlatformApiClient,
  PlatformApiError,
  bootstrapGameSession,
  resolveGameEndpoint,
  resolveGameSocketPath,
  useGamePlatformSession
};
//# sourceMappingURL=index.js.map